import { Link } from 'react-router-dom';
import HERO_IMAGES from '../heroImages';
import SEO, { SITE_URL } from '../components/SEO/SEO';
import { Globe, Smartphone, Zap, ArrowRight, CheckCircle2, Megaphone, ShieldAlert } from 'lucide-react';
import {
  // Languages
  SiPython, SiKotlin, SiJavascript, SiTypescript, SiCplusplus,
  SiGo, SiRust, SiPhp, SiRuby, SiSwift, SiDart,
  // Frontend
  SiHtml5, SiReact, SiAngular, SiNextdotjs, SiTailwindcss,
  SiFlutter, SiVuedotjs, SiSass, SiBootstrap, SiRedux, SiVite,
  // Backend
  SiNodedotjs, SiExpress, SiDjango, SiFlask, SiSpring, SiNestjs,
  SiFastapi, SiGraphql,
  // Database
  SiMysql, SiPostgresql, SiMongodb, SiSqlite, SiRedis, SiFirebase,
  SiSupabase,
  // DevOps & Cloud
  SiDocker, SiKubernetes, SiAmazonwebservices, SiGooglecloud,
  SiVercel, SiNetlify, SiNginx, SiGit, SiGithub, SiGitlab,
  // Tools & Testing
  SiSelenium, SiJest, SiCypress, SiPostman, SiFigma, SiJira,
  SiNotion, SiSlackware,
} from 'react-icons/si';
import { FaJava, FaCuttlefish, FaAws } from 'react-icons/fa';
import { DiCss3 } from 'react-icons/di';
import './ServicesPage.css';

const services = [
  {
    id: 'web',
    icon: <Globe size={28} />,
    title: 'Web Development',
    tagline: 'Enterprise-grade web platforms',
    color: '#0A4FFF',
    bg: 'https://images.unsplash.com/photo-1751738567808-6affa516fedc?fm=jpg&q=60&w=1200&auto=format&fit=crop',
    desc: 'We architect full-stack web applications — from marketing sites to complex SaaS platforms. React, Next.js, Node.js, Django — whatever your product needs.',
    features: [
      'Custom full-stack development',
      'Progressive Web Apps (PWA)',
      'API design & integration',
      'Performance optimisation',
      'SEO-ready architecture',
      'Admin dashboards & CMS',
    ],
  },
  {
    id: 'app',
    icon: <Smartphone size={28} />,
    title: 'App Development',
    tagline: 'Cross-platform mobile solutions',
    color: '#7C3AED',
    bg: 'https://images.unsplash.com/photo-1765046255505-083da4b856c8?fm=jpg&q=60&w=1200&auto=format&fit=crop',
    desc: 'iOS and Android apps built with React Native or Flutter — one codebase, two platforms, zero compromise on user experience.',
    features: [
      'React Native & Flutter',
      'Native iOS & Android modules',
      'Offline-first architecture',
      'Push notifications & deep links',
      'App Store & Play Store deployment',
      'Post-launch monitoring',
    ],
  },
  {
    id: 'automation',
    icon: <Zap size={28} />,
    title: 'Automation & Integrations',
    tagline: 'Work smarter, not harder',
    color: '#D97706',
    bg: 'https://images.unsplash.com/photo-1697577418970-95d99b5a55cf?fm=jpg&q=60&w=1200&auto=format&fit=crop',
    desc: 'We build custom automation pipelines — from workflow tools to enterprise ETL systems — that eliminate repetitive tasks and connect your software ecosystem.',
    features: [
      'Workflow automation (n8n, custom)',
      'Third-party API integrations',
      'Email & notification automation',
      'Data pipelines & ETL',
      'RPA (Robotic Process Automation)',
      'Scheduled job management',
    ],
  },
  {
    id: 'marketing',
    icon: <Megaphone size={28} />,
    title: 'Digital Marketing',
    tagline: 'Grow your reach, measurably',
    color: '#EC4899',
    bg: 'https://images.unsplash.com/photo-1686061593213-98dad7c599b9?fm=jpg&q=60&w=1200&auto=format&fit=crop',
    desc: 'From SEO to paid campaigns, we build data-driven marketing engines that turn traffic into customers — with clear reporting at every step.',
    features: [
      'SEO & content strategy',
      'Google & Meta ad campaigns',
      'Social media management',
      'Email marketing automation',
      'Conversion rate optimisation',
      'Analytics & performance reporting',
    ],
  },
  {
    id: 'cybersecurity',
    icon: <ShieldAlert size={28} />,
    title: 'Cyber Security Consulting',
    tagline: 'VAPT, defence & risk analysis',
    color: '#DC2626',
    bg: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?fm=jpg&q=60&w=1200&auto=format&fit=crop',
    desc: 'Comprehensive security consulting covering Vulnerability Assessment & Penetration Testing (VAPT), threat defence strategy, and ongoing risk analysis to keep your systems resilient.',
    features: [
      'Vulnerability Assessment & Penetration Testing (VAPT)',
      'Network & infrastructure defence',
      'Risk analysis & threat modelling',
      'Security audits & compliance reviews',
      'Incident response planning',
      'Employee security awareness training',
    ],
  },
];

const techCategories = [
  {
    label: 'Languages',
    accent: '#0A4FFF',
    items: [
      { name: 'Python', icon: <SiPython />, color: '#3776AB' },
      { name: 'Java', icon: <FaJava />, color: '#E76F00' },
      { name: 'JavaScript', icon: <SiJavascript />, color: '#F7DF1E' },
      { name: 'TypeScript', icon: <SiTypescript />, color: '#3178C6' },
      { name: 'C', icon: <FaCuttlefish />, color: '#A8B9CC' },
      { name: 'C++', icon: <SiCplusplus />, color: '#00599C' },
      { name: 'Go', icon: <SiGo />, color: '#00ADD8' },
      { name: 'Rust', icon: <SiRust />, color: '#CE422B' },
      { name: 'Kotlin', icon: <SiKotlin />, color: '#7F52FF' },
      { name: 'Swift', icon: <SiSwift />, color: '#F05138' },
      { name: 'Dart', icon: <SiDart />, color: '#0175C2' },
      { name: 'PHP', icon: <SiPhp />, color: '#777BB4' },
      { name: 'Ruby', icon: <SiRuby />, color: '#CC342D' },
    ],
  },
  {
    label: 'Frontend',
    accent: '#7C3AED',
    items: [
      { name: 'HTML5', icon: <SiHtml5 />, color: '#E34F26' },
      { name: 'CSS3', icon: <DiCss3 />, color: '#1572B6' },
      { name: 'Sass', icon: <SiSass />, color: '#CC6699' },
      { name: 'Tailwind', icon: <SiTailwindcss />, color: '#06B6D4' },
      { name: 'Bootstrap', icon: <SiBootstrap />, color: '#7952B3' },
      { name: 'React', icon: <SiReact />, color: '#61DAFB' },
      { name: 'Next.js', icon: <SiNextdotjs />, color: '#111111' },
      { name: 'Angular', icon: <SiAngular />, color: '#DD0031' },
      { name: 'Vue.js', icon: <SiVuedotjs />, color: '#4FC08D' },
      { name: 'Redux', icon: <SiRedux />, color: '#764ABC' },
      { name: 'Vite', icon: <SiVite />, color: '#646CFF' },
      { name: 'Flutter', icon: <SiFlutter />, color: '#02569B' },
    ],
  },
  {
    label: 'Backend',
    accent: '#059669',
    items: [
      { name: 'Node.js', icon: <SiNodedotjs />, color: '#339933' },
      { name: 'Express', icon: <SiExpress />, color: '#111111' },
      { name: 'NestJS', icon: <SiNestjs />, color: '#E0234E' },
      { name: 'Django', icon: <SiDjango />, color: '#092E20' },
      { name: 'Flask', icon: <SiFlask />, color: '#111111' },
      { name: 'FastAPI', icon: <SiFastapi />, color: '#009688' },
      { name: 'Spring', icon: <SiSpring />, color: '#6DB33F' },
      { name: 'GraphQL', icon: <SiGraphql />, color: '#E10098' },
    ],
  },
  {
    label: 'Databases',
    accent: '#D97706',
    items: [
      { name: 'PostgreSQL', icon: <SiPostgresql />, color: '#4169E1' },
      { name: 'MySQL', icon: <SiMysql />, color: '#4479A1' },
      { name: 'SQLite', icon: <SiSqlite />, color: '#003B57' },
      { name: 'MongoDB', icon: <SiMongodb />, color: '#47A248' },
      { name: 'Redis', icon: <SiRedis />, color: '#DC382D' },
      { name: 'Firebase', icon: <SiFirebase />, color: '#FFCA28' },
      { name: 'Supabase', icon: <SiSupabase />, color: '#3ECF8E' },
    ],
  },
  {
    label: 'DevOps & Cloud',
    accent: '#0891B2',
    items: [
      { name: 'AWS', icon: <FaAws />, color: '#FF9900' },
      { name: 'Google Cloud', icon: <SiGooglecloud />, color: '#4285F4' },
      { name: 'Docker', icon: <SiDocker />, color: '#2496ED' },
      { name: 'Kubernetes', icon: <SiKubernetes />, color: '#326CE5' },
      { name: 'Nginx', icon: <SiNginx />, color: '#009639' },
      { name: 'Vercel', icon: <SiVercel />, color: '#111111' },
      { name: 'Netlify', icon: <SiNetlify />, color: '#00C7B7' },
      { name: 'Git', icon: <SiGit />, color: '#F05032' },
      { name: 'GitHub', icon: <SiGithub />, color: '#181717' },
      { name: 'GitLab', icon: <SiGitlab />, color: '#FC6D26' },
    ],
  },
  {
    label: 'Tools & Testing',
    accent: '#E11D48',
    items: [
      { name: 'Selenium', icon: <SiSelenium />, color: '#43B02A' },
      { name: 'Jest', icon: <SiJest />, color: '#C21325' },
      { name: 'Cypress', icon: <SiCypress />, color: '#17202C' },
      { name: 'Postman', icon: <SiPostman />, color: '#FF6C37' },
      { name: 'Figma', icon: <SiFigma />, color: '#F24E1E' },
      { name: 'Jira', icon: <SiJira />, color: '#0052CC' },
      { name: 'Notion', icon: <SiNotion />, color: '#111111' },
      { name: 'Slack', icon: <SiSlackware />, color: '#4A154B' },
    ],
  },
];

const process = [
  { num: '01', title: 'Discovery Call', desc: 'We learn your goals and vision in a free 30-minute consultation.' },
  { num: '02', title: 'Scope & Estimate', desc: 'A detailed scope document with timeline, tech stack, and fixed pricing.' },
  { num: '03', title: 'Design Sprint', desc: 'Wireframes and UI prototypes reviewed before we write a line of code.' },
  { num: '04', title: 'Development', desc: 'Agile sprints with bi-weekly demos and full transparency.' },
  { num: '05', title: 'QA & Security', desc: 'Rigorous testing, security audits, and performance benchmarks.' },
  { num: '06', title: 'Launch & Support', desc: 'Smooth deployment with dedicated post-launch support.' },
];

export default function ServicesPage() {
  const servicesJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: services.map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Service',
        name: s.title,
        description: s.desc,
        provider: { '@type': 'ProfessionalService', name: 'ExploitEye', url: SITE_URL },
        areaServed: 'IN',
      },
    })),
  };

  return (
    <main className="services-page">
      <SEO
        title="Our Services — Web, App, Automation, Marketing & Cyber Security"
        description="Explore ExploitEye's services: Web Development, App Development, Automation & Integrations, Digital Marketing, and Cyber Security Consulting (VAPT, defence & risk analysis)."
        path="/services"
        keywords="web development services, app development services, automation integrations, digital marketing agency, cyber security consulting, VAPT services India"
        jsonLd={servicesJsonLd}
      />
      {/* HERO */}
      <section className="page-hero" style={{ backgroundImage: `url(${HERO_IMAGES.services})` }}>
        <div className="services-hero__glow" />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <span className="section-label">What We Do</span>
          <h1>
            Services Built for
            <br />
            <span className="gradient-text">Ambitious Entrepreneurs</span>
          </h1>
          <p>
            From idea to enterprise-grade platform — we cover every layer of the
            digital stack, with security and personalisation at the core.
          </p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="services-list">
        <div className="container">
          {services.map((s, i) => (
            <div
              key={s.id}
              id={s.id}
              className={`service-row ${i % 2 === 1 ? 'service-row--reverse' : ''}`}
            >
              <div className="service-row__content">
                <div className="service-row__icon" style={{ '--s-color': s.color }}>
                  {s.icon}
                </div>
                <p className="service-row__tagline" style={{ color: s.color }}>
                  {s.tagline}
                </p>
                <h2 className="service-row__title">{s.title}</h2>
                <p className="service-row__desc">{s.desc}</p>
                <Link
                  to="/contact"
                  className="btn-outline"
                  style={{ color: s.color, borderColor: s.color }}
                >
                  Discuss Your Project <ArrowRight size={15} />
                </Link>
              </div>
              <div className="service-row__features">
                <div className="service-features-card" style={{ '--s-color': s.color, '--s-bg': `url(${s.bg})` }}>
                  <h4>What's Included</h4>
                  <ul>
                    {s.features.map((f, j) => (
                      <li key={j}>
                        <CheckCircle2 size={15} style={{ color: s.color }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TECH STACK — ELEGANT VERSION */}
      <section className="tech-stack-section">
        <div className="tech-bg-orb tech-bg-orb--1" />
        <div className="tech-bg-orb tech-bg-orb--2" />
        <div className="container">
          <div className="section-center tech-stack-header">
            <span className="section-label">Our Toolbox</span>
            <h2 className="section-heading" style={{ textAlign: 'center' }}>
              Tech Stack We <span className="gradient-text">Master</span>
            </h2>
            <p
              className="section-subheading"
              style={{ textAlign: 'center', margin: '16px auto 0' }}
            >
              A curated arsenal of battle-tested tools, frameworks, and platforms —
              hand-picked for performance, scalability, and longevity.
            </p>
          </div>

          <div className="tech-categories">
            {techCategories.map((cat) => (
              <div
                key={cat.label}
                className="tech-category"
                style={{ '--cat-accent': cat.accent }}
              >
                <div className="tech-category__header">
                  <span className="tech-category__dot" />
                  <h4 className="tech-category__label">{cat.label}</h4>
                  <span className="tech-category__count">
                    {String(cat.items.length).padStart(2, '0')}
                  </span>
                  <span className="tech-category__line" />
                </div>
                <div className="tech-grid">
                  {cat.items.map((t) => (
                    <div
                      key={t.name}
                      className="tech-chip"
                      style={{ '--t-color': t.color }}
                      title={t.name}
                    >
                      <span className="tech-chip__icon">{t.icon}</span>
                      <span className="tech-chip__name">{t.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="services-process">
        <div className="container">
          <div className="section-center">
            <span className="section-label">How We Work</span>
            <h2 className="section-heading" style={{ textAlign: 'center' }}>
              Our Delivery Process
            </h2>
            <p
              className="section-subheading"
              style={{ textAlign: 'center', margin: '16px auto 60px' }}
            >
              Six clear steps from your first call to a live product.
            </p>
          </div>
          <div className="process-grid">
            {process.map((p, i) => (
              <div key={i} className="process-step">
                <div className="process-step__num">{p.num}</div>
                <h4>{p.title}</h4>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-strip">
        <div className="container">
          <div className="cta-strip-inner">
            <div className="cta-strip-text">
              <h2>Not Sure Which Service You Need?</h2>
              <p>Book a free 30-minute discovery call. We'll help you figure it out.</p>
            </div>
            <div className="cta-strip-actions">
              <Link to="/contact" className="btn-primary">
                Book Free Consultation <ArrowRight size={16} />
              </Link>
              <Link to="/pricing" className="btn-ghost">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}